import { useQuery } from "@tanstack/react-query";
import { CURRENT_CONSENT_VERSION, type MeResponse, type PolicyBundle, type PolicyId } from "@workcost/shared";
import { apiFetch, apiJson } from "./api";
import { logout, sanitizeReturnTo, setAccessToken } from "./auth";
import { EstimateApiError } from "./estimateApi";
import { clearOutbox } from "./outbox";
import { clearAllLocal } from "./photoStore";
import { safeStorage } from "./safe-storage";

/** เฉพาะ dev และ VITE_MOCK_API=1 (production ตัดทิ้ง) */
async function loadMockConsent(): Promise<typeof import("../mock/consent") | null> {
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "1") return import("../mock/consent");
  return null;
}

/** ผู้ใช้ยินยอมนโยบายเวอร์ชันปัจจุบันแล้วหรือยัง (server ตรวจซ้ำทุกครั้งที่ใช้ AI/อัปโหลด) */
export function requiredVersion(me: Pick<MeResponse, "requiredConsentVersion"> | undefined | null): string {
  return me?.requiredConsentVersion ?? CURRENT_CONSENT_VERSION;
}

export function hasCurrentConsent(me: Pick<MeResponse, "consentAcceptedAt" | "consentVersion" | "requiredConsentVersion"> | undefined | null): boolean {
  return Boolean(me?.consentAcceptedAt) && me?.consentVersion === requiredVersion(me);
}

export function consentUrl(returnTo: string): string {
  return `/consent?returnTo=${encodeURIComponent(sanitizeReturnTo(returnTo, "/"))}`;
}

/** POST /me/consent */
export async function postConsent(version: string = CURRENT_CONSENT_VERSION): Promise<{ consentAcceptedAt: string; consentVersion: string }> {
  const mock = await loadMockConsent();
  if (mock) return mock.acceptConsent(version);
  return apiJson<{ consentAcceptedAt: string; consentVersion: string }>("/me/consent", { method: "POST", json: { version } });
}

/** DELETE /me {confirm:"DELETE"} -> 202 (server ลบเบื้องหลัง) */
export async function deleteAccountRequest(): Promise<void> {
  const mock = await loadMockConsent();
  if (mock) {
    await mock.deleteAccount();
    return;
  }
  const res = await apiFetch("/me", { method: "DELETE", json: { confirm: "DELETE" } });
  if (!res.ok) throw new EstimateApiError(res.status, "delete_failed");
}

/** key ใน localStorage ที่เป็นค่าตั้งของหน้าจอ (ภาษา ธีม) ไม่ใช่ข้อมูลส่วนบุคคล เก็บไว้หลังลบบัญชี */
const KEEP_LOCAL = new Set(["wc.lang", "wc.theme"]);

/** ล้างข้อมูลในเครื่องทั้งหมดของบัญชีนี้: IndexedDB (รูป คิว outbox) sessionStorage localStorage ที่ขึ้นต้น wc. และ token ในหน่วยความจำ */
export async function clearLocalData(): Promise<void> {
  setAccessToken(null);
  try {
    await clearAllLocal();
  } catch {
    /* ไม่มี IndexedDB */
  }
  await clearOutbox();
  try {
    window.sessionStorage.clear();
  } catch {
    /* ignore */
  }
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k?.startsWith("wc.") && !KEEP_LOCAL.has(k)) keys.push(k);
    }
    for (const k of keys) safeStorage.set(k, null);
  } catch {
    /* ignore */
  }
}

/** หลัง server ลบบัญชีแล้ว: ล้างข้อมูลในเครื่องและล้าง cookie เข้าสู่ระบบ (ล้มเหลวได้เพราะ session ถูกลบไปแล้ว) */
export async function finishAccountDeletion(): Promise<void> {
  await clearLocalData();
  await logout().catch(() => false);
}

/** GET /config (สาธารณะ): ข้อมูลผู้ควบคุมข้อมูลที่ Admin ตั้งไว้ (null = ยังไม่ตั้ง) */
export interface PublicConfig {
  default_overhead_pct?: number;
  legal_name?: string | null;
  legal_address?: string | null;
  legal_tax_id?: string | null;
  legal_phone?: string | null;
  support_email?: string | null;
  support_email_2?: string | null;
}

export function usePublicConfig() {
  return useQuery({
    queryKey: ["public-config"],
    queryFn: async (): Promise<PublicConfig> => {
      const mock = await loadMockConsent();
      if (mock) return mock.publicConfig();
      return apiJson<PublicConfig>("/config", { auth: false });
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** GET /policy (สาธารณะ): นโยบายทุกฉบับ ฉบับที่ custom=false คือใช้ข้อความตั้งต้นของแอป */
export function usePolicy() {
  return useQuery({
    queryKey: ["public-policy"],
    queryFn: async (): Promise<PolicyBundle> => {
      const mock = await loadMockConsent();
      if (mock) return mock.policy();
      return apiJson<PolicyBundle>("/policy", { auth: false });
    },
    staleTime: 60 * 1000,
  });
}

/** ข้อความของนโยบายหนึ่งฉบับตามภาษาที่เลือก (undefined = ใช้ข้อความตั้งต้น) */
export function customPolicyText(bundle: PolicyBundle | undefined, id: PolicyId, lang: "th" | "en"): string | undefined {
  const d = bundle?.docs?.[id];
  if (!d?.custom) return undefined;
  return (lang === "en" ? d.en : d.th) || d.th || undefined;
}
